import classnames from 'classnames';
import React from 'react';
import { TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import {
  CapturedImagesIcon as capturedImagesIcon,
  ReviewedIcon as reviewMarkIcon,
} from '@/shared/ui';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { isValidHr } from '@/entities/heart-rate/@x/event';
import './styles.scss';
import useThumbnailEvent from './useThumbnailEvent';

export type ThumbnailEventProps = {
  overlayContent?: React.ReactNode;
  id: number | string;
  onClickSelect?: () => void;
  // Optional: the useEffect below only calls drawStrip when ecgData?.length > 0,
  // and width/height are otherwise only passed straight through as SVG attrs.
  ecgData?: number[][];
  channels?: number[];
  gain?: number;
  speed?: number;
  width?: number;
  height?: number;
  isSelected?: boolean;
  isActive?: boolean;
  isChanged?: boolean;
  disabled?: boolean;
  className?: string;
  time?: string | number;
  duration?: string;
  isCaptured?: boolean;
  typeRight?: React.ReactNode;
  timezoneOffset?: number;
  startReport?: string | number;
  isReviewed?: boolean;
  highPass?: number;
  isBeatEvent?: boolean;
  eventType?: any;
  minHr?: number;
  maxHr?: number;
  avgHr?: number;
  isNoHrEvent?: boolean;
  countBeats?: number;
  beatMarkers?: number[];
  hesBeatStatus?: number[];
};

/**
 * Renders one selectable ECG event thumbnail with its SVG strip and corner captions. Draws through the canvas ref returned by useThumbnailEvent, marks the button active, changed or selected via class names, lays out the beat-count and time, duration and HR captions in the corners, and shows the captured and reviewed icons at the top right.
 *
 * @param props - Thumbnail props such as id, channels, width, height, time, duration, the HR values, the selection flags and onClickSelect.
 * @returns The rendered thumbnail button with its strip and captions.
 */
const ThumbnailEvent = ({
  overlayContent = null,
  id,
  onClickSelect,
  ecgData,
  channels = [2],
  gain,
  speed,
  width,
  height,
  isSelected,
  isActive,
  isChanged,
  disabled = false,
  className,
  time,
  duration,
  isCaptured,
  typeRight,
  timezoneOffset,
  startReport,
  isReviewed,
  highPass,
  isBeatEvent,
  eventType,
  minHr,
  maxHr,
  avgHr,
  isNoHrEvent,
  countBeats,
  beatMarkers,
  hesBeatStatus,
}: ThumbnailEventProps) => {
  const { canvasRef } = useThumbnailEvent({
    ecgData,
    channels,
    gain,
    speed,
    width,
    height,
    highPass,
    beatMarkers,
    hesBeatStatus,
  });

  /**
   * Formats an event thumbnail's timestamp together with its study day number. Converts the strip time to report local time and, when a report start is known, appends the one-based day offset between the two calendar days in parentheses.
   *
   * @returns The time formatted as HH:mm:ss with an optional day number.
   */
  const renderTime = () => {
    const stripDate = convertToMomentJs(time, timezoneOffset);
    const dateStartReport = convertToMomentJs(startReport, timezoneOffset).startOf('days');
    const stringStartReport = startReport
      ? ` (${stripDate.clone().startOf('days').diff(dateStartReport, 'days') + 1})`
      : '';
    return stripDate.format('HH:mm:ss') + stringStartReport;
  };

  /**
   * Builds the beat-count caption shown above an ECG event thumbnail. Pushes a singular or plural beats label when countBeats is set, then interleaves dividers if more than one part was collected.
   *
   * @returns The top-left caption nodes.
   */
  const renderTopLeftCorner = () => {
    let resultHtml: React.ReactNode[] = [];
    if (countBeats) {
      resultHtml.push(<div>{`${countBeats} ${countBeats > 1 ? 'beats' : 'beat'}`}</div>);
    }
    if (resultHtml.length > 1) {
      resultHtml = resultHtml.flatMap((value, index, array) =>
        array.length - 1 !== index ? [value, <div className="divider" />] : value,
      );
    }
    return resultHtml;
  };

  /**
   * Builds the caption row shown under an ECG event thumbnail. Collects the formatted time, the channel label when only one channel is drawn, the duration for non-beat events, and either an average HR for AFib or a min, avg and max HR line, then interleaves dividers between the collected parts.
   *
   * @returns The caption nodes with dividers between them.
   */
  const renderBottomLeftCorner = () => {
    let resultHtml: React.ReactNode[] = [];
    const formatDuration = duration || '00s';
    if (time) {
      resultHtml.push(<div>{renderTime()}</div>);
    }
    if (channels?.length === 1) {
      resultHtml.push(<div>{`CH${channels[0]}`}</div>);
    }
    if (formatDuration && !isBeatEvent) {
      resultHtml.push(<div>{`Duration: ${formatDuration}`}</div>);
    }
    if ((avgHr || maxHr || minHr) && !isNoHrEvent) {
      resultHtml.push(
        <div>
          {eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB
            ? `Avg HR: ${isValidHr(avgHr) ? avgHr : 'NA'}`
            : `HR Min: ${isValidHr(minHr) ? minHr : 'NA'} - Avg: ${isValidHr(avgHr) ? avgHr : 'NA'} - Max: ${isValidHr(maxHr) ? maxHr : 'NA'}`}
        </div>,
      );
    }
    if (resultHtml.length > 1) {
      resultHtml = resultHtml.flatMap((value, index, array) =>
        array.length - 1 !== index ? [value, <div className="divider" />] : value,
      );
    }
    return resultHtml;
  };

  return (
    <button
      id={id as string}
      disabled={disabled}
      className={classnames(
        'single-strip-event',
        className,
        isActive ? '--active' : '',
        isChanged ? '--changed' : '',
        isSelected ? '--selected' : '',
      )}
      onClick={onClickSelect}
    >
      {overlayContent}
      <svg ref={canvasRef} width={width} height={height} />
      <div className="top-left-corner">{renderTopLeftCorner()}</div>
      <div className="line-bottom" />
      <div className="bottom-left-corner">{renderBottomLeftCorner()}</div>
      <div className="bottom-right-corner">
        {typeRight && <div className="strip-right">{typeRight}</div>}
      </div>
      <div className="top-right-corner">
        {isCaptured && <img src={capturedImagesIcon} alt="Captured images icon" />}
        {isReviewed && <img src={reviewMarkIcon} alt="Reviewed images icon" />}
      </div>
    </button>
  );
};

export default ThumbnailEvent;
